import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShieldCheck } from "lucide-react";

import LiveLayout from "./LiveLayout";
import D13Footer from "../preview/D13Footer";
import HeroMedia from "./HeroMedia";
import { Star } from "./Star";
import { cld } from "../../lib/cloudinary";
import { HERO_VIDEO } from "../../lib/heroVideo";
import { EMPLOYER_STEPS, EMPLOYER_STATS, FEATURED_SECTORS, SECTOR_COUNT, FAQ_ITEMS } from "../../lib/adelContent";

import "../preview/Design13.css";
import "../preview/Design13Audience.css";
import "./Editorial.css";

/* ============================================================
   EmployersHome — SHORT teaser landing for /employers.
   Hero (Request workers / How it works) → 4 teasers linking to
   dedicated pages → close. Calm, capable "we own the pipeline" voice.
   ============================================================ */

const sectorMini = FEATURED_SECTORS.slice(0, 4);

export default function EmployersHome() {
  const heroRef = useRef(null);
  const { scrollYProgress: heroP } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroScale = useTransform(heroP, [0, 1], [1, 1.1]);
  const heroVeil = useTransform(heroP, [0, 1], [0.5, 0.82]);
  const heroTextY = useTransform(heroP, [0, 1], [0, -40]);

  return (
    <LiveLayout audience="employer">
      {/* HERO — Request workers / How it works */}
      <section ref={heroRef} className="d13-hero d13-aud-hero d13-aud-hero--employer">
        <HeroMedia video={HERO_VIDEO.employer} className="d13-hero__img"
          alt="Adel Group — quality Kenyan candidates ready for Gulf employers" />
        <motion.div className="d13-hero__veil" style={{ opacity: heroVeil }} />
        <motion.div className="d13-hero__inner" style={{ y: heroTextY }}>
          <div className="d13-container">
            <motion.span className="d13-eyebrow d13-eyebrow--on-dark ed-eyebrow"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
              <Star className="ed-eyebrow__star" /> For employers
            </motion.span>
            <motion.h1 className="d13-hero__title"
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}>
              We source. <em>You choose.</em>
            </motion.h1>
            <motion.p className="d13-hero__sub"
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.45 }}>
              We own the whole pipeline — sourcing, documentation, visas and travel. We can mobilise candidates <em>in as little as 14 days.</em>
            </motion.p>
            <motion.div className="d13-hero__ctas d13-hero__ctas--split"
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.65 }}>
              <Link to="/employers/brief" className="d13-btn d13-btn--primary">Request workers <ArrowRight size={16} strokeWidth={2.2} /></Link>
              <Link to="/employers/how-it-works" className="d13-btn d13-btn--outline">How it works <ArrowUpRight size={16} strokeWidth={2.2} /></Link>
            </motion.div>
            <motion.span className="hero-trust"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.85 }}>
              <ShieldCheck size={15} strokeWidth={2.2} /> Licensed recruitment agency · Handled end to end
            </motion.span>
          </div>
        </motion.div>
      </section>

      {/* HOW IT WORKS — register cream */}
      <section id="process" className="ed-band ed-band--cream ed-pad">
        <div className="ed-container">
          <header className="ed-opener">
            <div className="ed-opener__row">
              <span className="ed-eyebrow"><Star className="ed-eyebrow__star" /> How it works</span>
              <Link to="/employers/how-it-works" className="ed-more">See how it works →</Link>
            </div>
            <h2 className="ed-h2">Simple, <em>from brief to arrival.</em></h2>
          </header>
          <div className="jt-cards jt-cards--steps">
            {EMPLOYER_STEPS.map((s) => (
              <Link key={s.n} to="/employers/how-it-works" className="jt-card">
                <img src={cld(s.image, { w: 800, q: "auto", f: "auto" })} alt={s.alt} loading="lazy" />
                <div className="jt-card__scrim" />
                <div className="jt-card__meta">
                  <span className="jt-card__kicker"><Star /> Step {s.n}</span>
                  <span className="jt-card__name">{s.t}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CLIENTS STAY TEASER — register sand (stats) */}
      <section id="why" className="ed-band ed-band--sand ed-pad">
        <div className="ed-container">
          <header className="ed-opener">
            <div className="ed-opener__row">
              <span className="ed-eyebrow"><Star className="ed-eyebrow__star" /> Why clients stay</span>
              <Link to="/employers/why-adel" className="ed-more">Why partners stay →</Link>
            </div>
            <h2 className="ed-h2">The right people, <em>first time.</em></h2>
          </header>
          <ol className="land-stats">
            {EMPLOYER_STATS.map((c) => (
              <motion.li key={c.l} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.5 }} transition={{ duration: 0.55 }}>
                <span className="ed-numeral">{c.v.toLocaleString()}{c.s}</span>
                <span className="land-stats__l">{c.l}</span>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      {/* SECTORS MINI — register cream */}
      <section id="sectors" className="ed-band ed-band--cream ed-pad">
        <div className="ed-container">
          <header className="ed-opener">
            <div className="ed-opener__row">
              <span className="ed-eyebrow"><Star className="ed-eyebrow__star" /> What we staff</span>
              <Link to="/employers/sectors" className="ed-more">All {SECTOR_COUNT} sectors →</Link>
            </div>
            <h2 className="ed-h2">Seventeen sectors, <em>one standard.</em></h2>
          </header>
          <div className="sec-mini">
            {sectorMini.map((s) => (
              <Link key={s.slug} to="/employers/sectors" className="sec-mini__tile">
                <img src={cld(`adel/v2/sector-${s.slug}`, { w: 600, q: "auto", f: "auto" })} loading="lazy" alt="" />
                <div className="sec-mini__tile-scrim" />
                <span>{s.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* COMMON QUESTIONS — register sand (mirrors /faq styling; embeds answer-first Q&A for AEO) */}
      <section className="ed-band ed-band--sand ed-pad">
        <div className="ed-container">
          <header className="ed-opener">
            <div className="ed-opener__row">
              <span className="ed-eyebrow"><Star className="ed-eyebrow__star" /> Common questions</span>
              <Link to="/faq" className="ed-more">See all questions →</Link>
            </div>
            <h2 className="ed-h2">Answers, <em>before you ask.</em></h2>
          </header>
          <div className="faq-list">
            {FAQ_ITEMS.filter((f) => f.audience === "employer").slice(0, 4).map((f) => (
              <div key={f.q} className="faq-item">
                <h3 className="faq-q">{f.q}</h3>
                <p className="faq-a">{f.a}</p>
                {f.link && (
                  <p className="faq-a faq-verify">
                    <a href={f.link.href} target="_blank" rel="noreferrer">{f.link.label} →</a>
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSE — register navy */}
      <section className="ed-band ed-band--ink ed-pad">
        <div className="ed-container ed-cta">
          <motion.h2 className="ed-cta__t" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }} transition={{ duration: 0.8 }}>
            Tell us who you need.
          </motion.h2>
          <p className="ed-cta__p">We'll come back in writing within 48 hours with realistic salary bands, lead times and a starter shortlist.</p>
          <div className="ed-cta__row">
            <Link to="/employers/brief" className="d13-btn d13-btn--primary">Send your brief <ArrowRight size={16} strokeWidth={2.2} /></Link>
            <a href="tel:+254117222777" className="d13-btn d13-btn--ghost">Call +254 117 222 777</a>
          </div>
        </div>
      </section>

      <D13Footer />
    </LiveLayout>
  );
}
